import { UUID, Color } from '@lite-workspace/dto-fotoclass-common';
import { PredefinedFileTags } from '@lite-workspace/dto-fotoclass-photo';
import type { BezierPath, LineCapType, LineJoinType, LineAlignment } from 'ag-psd';
import type { PsdLayerNode, PsdLayerLockState, PsdLayerColorLabel } from './psd-layer-node';
import type { AdjustmentBoxParams, AdjustmentBinaryResource } from './adjustment-params';

/** `firstBaselineRule` of an InDesign frame's First Baseline Offset; render-rule.json `inddFirstBaselineRules` referees it. */
export const INDD_FIRST_BASELINE_RULES = ['typoAscent', 'capHeight', 'xHeight', 'emBox', 'leading', 'fixed'] as const;
export type InddFirstBaselineRule = (typeof INDD_FIRST_BASELINE_RULES)[number];
export type { BezierPath, LineCapType, LineJoinType, LineAlignment };

/**
 * Цвета печатной модели встроенных шаблонов: заливка пустой рамки и цвет служебного текста.
 * Это не UI-палитра — значения уезжают в PSD-подобную модель и рендерятся движком печати,
 * поэтому живут рядом с моделью бокса, а не среди токенов темы.
 */
export const BUILT_IN_TEMPLATE_PHOTO_FILL = '#E6E6E6';
export const BUILT_IN_TEMPLATE_TEXT_COLOR = '#666666';

export enum TextTypeEnum {
  FREE_TEXT = 'freeText',
  SCHOOL = 'school',
  GRADUATION_YEAR = 'graduationYear',
  CITY = 'city',
  CLASS = 'class',
  PERSON_FIRST_NAME = 'personFirstName',
  PERSON_LAST_NAME = 'personLastName',
  PERSON_FULL_NAME = 'personFullName',
  PERSON_GIVEN_PATRONYMIC = 'personGivenPatronymic',
  PERSON_PATRONYMIC = 'personPatronymic',
  PERSON_QUOTE = 'personQuote',
  PERSON_SUBJECT = 'personSubject',
}

export enum GroupPeopleType {
  STUDENT = 'student',
  TEACHER = 'teacher',
  CLASS_TEACHER = 'classTeacher',
  OWNER = 'owner',
  // Организационная группа: не привязана к человеку, исключена из виньеточных расчётов.
  FREE = 'free',
  // Рамка «группа со владельцем»: кадр, где владелец альбома вместе с большой группой класса.
  GROUP_WITH_OWNER = 'groupWithOwner',
}

/** Место человека из списка проекта: фото и ФИО идут из карточки персоны. `owner` не входит — его кадр выбирает пользователь. */
export function isRosterCellRole(peopleType: string | null | undefined): boolean {
  return (
    peopleType === GroupPeopleType.STUDENT ||
    peopleType === GroupPeopleType.TEACHER ||
    peopleType === GroupPeopleType.CLASS_TEACHER
  );
}

/**
 * Роль ОТДЕЛЬНОЙ фоторамки: какой род кадра она принимает (ученик — отказ, фотограф — вопрос,
 * генерация — закон). Старшинство при чтении: роль рамки → принятый род группы → род группы;
 * рамка без роли судится по группе, как раньше.
 */
export enum ImageFrameRole {
  OWNER_PORTRAIT = 'ownerPortrait',
  WITH_OWNER = 'withOwner',
  WITHOUT_OWNER = 'withoutOwner',
  /** Кадр, где есть ≥80% учеников проекта с альбомами (педагоги не считаются). */
  COMMON_PHOTO = 'commonPhoto',
  /**
   * Кадр, который поставил фотограф и менять его некому: закреп — это роль ПЛЮС собственный кадр рамки,
   * отдельного поля у него нет. Шаблоны закреп не несут: кадра в шаблоне нет, а без кадра закрепа не бывает.
   */
  PINNED = 'pinned',
}

/**
 * Одни слова у меню типа рамки, метки выделенной рамки и отказов — второго словаря нет.
 * Порядок записей — порядок пунктов меню в обоих редакторах.
 */
export const IMAGE_FRAME_ROLE_LABELS: Record<ImageFrameRole, string> = {
  [ImageFrameRole.OWNER_PORTRAIT]: 'Мой портрет',
  [ImageFrameRole.WITH_OWNER]: 'Фото со мной',
  [ImageFrameRole.WITHOUT_OWNER]: 'Фото без меня',
  [ImageFrameRole.COMMON_PHOTO]: 'Общее фото',
  [ImageFrameRole.PINNED]: 'Закрепить фото',
};

export enum VignetteTextTypeEnum {
  TITLE = 'vignetteTitle',
  SUBTITLE = 'vignetteSubtitle',
  SUBTITLE2 = 'vignetteSubtitle2',
  SUBTITLE3 = 'vignetteSubtitle3',
  /** Цитата классрука вне его ячейки («Как все», R22): заполняется от ведущего, не от владельца альбома. */
  LEAD_QUOTE = 'vignetteLeadQuote',
  /** Подпись «Имя Отчество Фамилия» классрука под его цитатой. */
  LEAD_SIGNATURE = 'vignetteLeadSignature',
  /** «Пустые места → Цитата класса» (R217): текст живёт в ручке `vignette.classQuote`, не в рамке. */
  CLASS_QUOTE = 'vignetteClassQuote',
}

/**
 * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
 * template-authoring path (spread-to-template.ts) must never write these
 */
export type AssignedPhotoMeta = {
  filename?: string;
  exifDate?: string;
  dominantColor?: string;
  thumbHash?: string;
  personId?: string | null;
  tags?: readonly string[];
  faces?: readonly unknown[];
  /**
   * Адреса нарезанных версий фото, каждый под именем своей длинной стороны: THUMB 320, SMALL 640,
   * MEDIUM 1280, LARGE 1920. Живут здесь, а не в `versions`: тот требует на каждый тир ещё width,
   * height и fileId, которых у модели альбома нет, а выдумывать их — та же ложь о размере.
   * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
   * template-authoring path (spread-to-template.ts) must never write this
   */
  renditions?: {
    thumb?: string;
    small?: string;
    medium_1280?: string;
    big_1920?: string;
  };
};

export type AssignedPhotoPayload = {
  photoId: string;
  url: string;
  nativeWidth: number;
  nativeHeight: number;
  personBboxes?: readonly {
    x: number;
    y: number;
    width: number;
    height: number;
    /**
     * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
     * template-authoring path (spread-to-template.ts) must never write this
     */
    confidence: number;
  }[];
  /**
   * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
   * template-authoring path (spread-to-template.ts) must never write this
   */
  versions?: ObjectAssetVersions;
  /**
   * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
   * template-authoring path (spread-to-template.ts) must never write this
   */
  meta?: AssignedPhotoMeta;
};

/** Placement of compatibility-mask pixels in the box's parent coordinate space, independent of source pixels. */
/** transform maps normalized mask UV into parent coordinates; rectangle fields are its AABB. */
export type BoxMaskRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  transform?: [number, number, number, number, number, number];
  /** The mask rides every move of its own box instead of standing while the box slides under it. */
  follows?: true;
  /**
   * The frame is the box's own mask, not a clipping base's: when the box is flipped, the renderer mirrors the
   * frame and the mask pixels with it. The frame itself is never mirrored in the data. Without the mark the frame
   * stands where its base layer lies.
   */
  mirrors?: true;
};

/** Whether a mask window rides every move, turn and resize of its own box; a window without the mark stands. */
export function maskRidesBox(rect: BoxMaskRect | undefined): rect is BoxMaskRect & { follows: true } {
  return rect?.follows === true;
}

/**
 * Whether the frame mirrors with its flipped box: marked as the box's own (`mirrors`) or a riding window, which
 * is always the box's own. Twin of the psd-render adapter's `own_mask_frame`.
 */
export function maskMirrorsWithBox(rect: BoxMaskRect | undefined): boolean {
  return rect?.mirrors === true || maskRidesBox(rect);
}

export type ImageBoxParams = {
  fileTag?: PredefinedFileTags;
  /** Роль рамки (S12): принимаемый род кадра; нет поля — рамка судится по роду своей группы. */
  frameRole?: ImageFrameRole;
  /**
   * Reference to a TemplateSmartObjectSource (by id) whose embedded image is shown
   * as preview when no user photo has been dropped yet. Resolved to URL at runtime.
   */
  sourceRef?: string;
  /**
   * Base64 PNG of the alpha-clipping mask (decorative photo frame silhouette from PSD).
   * Applied via destination-in over whatever image is currently rendered — preview
   * or user-uploaded — so the photo takes the silhouette of the mask.
   * Legacy inline form; new imports persist the mask by reference in `maskUrl`/`maskFileId`.
   */
  maskDataUrl?: string;
  maskRect?: BoxMaskRect;
  /** Uploaded alpha-clipping mask (alpha-only PNG in storage); preferred over `maskDataUrl`. */
  maskUrl?: string;
  maskFileId?: string;
  /**
   * Обтравочная маска как в Photoshop: id слоя-декора НИЖЕ рамки, чьей альфой она вырезана. Форма и эффекты
   * (впечатанные в декор) принадлежат этому слою; рамка лишь вставлена в него. Снятие маски (`clearImageMask`)
   * возвращает рамке прямоугольник и ничего не удаляет — слой-маска остаётся как был.
   */
  maskBaseBoxId?: string;
  /**
   * Форма рамки пришла ОТ СЛОЯ: силуэт его собственных пикселей, который «Тип элемента → Рамка изображения»
   * забрал у декора. Такую форму снимать нечем и незачем — она и есть слой; меню предлагает «Убрать форму»
   * только для маски, которую пользователь сам выбрал в панели «Маски».
   */
  maskFromLayer?: boolean;
  /**
   * Собственная картинка декора, отложенная на время жизни рамкой. Без неё обратный «Тип элемента → Элемент
   * декора» возвращал пустой бокс: пиксели слоя рамке не нужны и выбрасываются, а взять их обратно неоткуда.
   */
  decorSource?: FrameDecorSource;
  /** @deprecated use `versions.thumbHash`. Removed in Phase 3. */
  previewThumbHash?: string;
  /** @deprecated TRANSIENT base64; persisted asset lives in `versions`. Removed in Phase 3. */
  previewPixelData?: string;
  /** Persisted asset: S3 paths (thumb/medium) + thumbHash placeholder. */
  versions?: ObjectAssetVersions;
  /**
   * Vector clipping paths (rounded photo-frame silhouette from PSD `vectorMask`).
   * When present, the image renderer clips photo + maskDataUrl + overlays to this path
   * via Konva clipFunc, so non-rectangular photo frames (rounded corners, custom shapes)
   * keep their silhouette regardless of user-uploaded photo aspect.
   */
  paths?: readonly BezierPath[];
  /** Decorative bitmap frame (uploaded PNG asset) drawn over the photo. */
  frameUrl?: string;
  frameFileId?: string;
  /**
   * Photo window inside the frame image, as fractions of the frame's own size (0..1).
   * Renderers stretch the frame so this window maps exactly onto the box rect
   * (PrintBox semantics); normalized units make the mapping independent of the
   * frame PNG's pixel dimensions.
   */
  frameWindow?: { x: number; y: number; width: number; height: number };
  /** Photo fit inside the box; absent = cover-fill (scale to cover, crop). `contain` keeps the whole photo visible. */
  fit?: 'contain';
  /** User photo placed in this slot. `null` means explicitly cleared. */
  assignedPhoto?: AssignedPhotoPayload | null;
  /**
   * Кадр, погашенный спором ученика «это не я»: рамка пуста, но помнит, что в ней стояло.
   * Подтверждение фотографа «да, это он» приходит в другой сеанс — вернуть кадр в ту же рамку можно
   * только по этой памяти. Любая обычная постановка кадра в рамку память стирает.
   */
  disputedPhoto?: AssignedPhotoPayload;
  /**
   * Crop/pan/zoom of `assignedPhoto` inside the frame: position, size and rotation in canvas units.
   * populated/consumed only by @lite-workspace/photobook-model persist-normalizer;
   * template-authoring path (spread-to-template.ts) must never write this
   */
  photoPlacementRect?: { left: number; top: number; width: number; height: number; angle?: number };
  /** Mirror the placed photo horizontally. */
  flipHorizontal?: boolean;
  /** Mirror the placed photo vertically. */
  flipVertical?: boolean;
  /**
   * A detected face sits on the spread fold (`detectFaceOnFoldIssues`, book-assembly) — the editor draws a
   * warning badge over the frame until `faceOnFoldAccepted`. Written only when true: an absent flag and an
   * explicit `false` mean the same thing, and the vast majority of frames carry neither.
   */
  hasFaceOnFold?: boolean;
  /** The user looked at the face-on-fold warning and kept the crop — suppresses the badge. */
  faceOnFoldAccepted?: boolean;
  /**
   * The PSD template baked this frame's outline into a sibling overlay tile, so the album default line
   * must not be drawn a second time on top of it (the frame's OWN stroke effect still paints). Stamped
   * once, when a template is projected onto a spread (`scaleBoxesToPage`), because only there the source
   * boxes still carry the template ids that pair a frame with its tile. Written only when true: an absent
   * flag and an explicit `false` mean the same thing.
   */
  frameStrokeBaked?: boolean;
  /**
   * Скругление углов рамки в книжных px — настройка редактора, из PSD не читается: назначенная рамка
   * всегда начинает с прямых углов, а форму слоя, если она есть, несёт `paths`. По одному силуэту идут
   * и обрезка фото, и обводки, и внутренние тени, поэтому радиус живёт на рамке, а не в эффектах.
   */
  cornerRadius?: number;
  /**
   * Эффекты слоя, как их разобрал парсер, — снимок макета. На рамке он только справочный: рисует движок
   * живые `effects`, а отсюда их возвращает «Вернуть как в макете». Правки редактора его не трогают,
   * поэтому удалённый в панели эффект из него не воскресает.
   */
  rawEffects?: BoxEffects;
  /** Чем слой был до назначения рамкой — по нему «Элемент декора» возвращает его прежний вид. */
  decorIntent?: DecorIntent;
};

/**
 * Снимок декора, снятый в момент назначения рамкой.
 *
 * Рамка рисует обводку и тень живыми, поэтому назначение выбрасывает и форму фигуры, и служебные
 * плитки, которыми парсер запёк эти эффекты вокруг слоя. Без снимка обратный ход давал пустой бокс:
 * возвращать было нечего, и слой пропадал с разворота.
 */
export type DecorIntent = {
  /** Параметры фигуры, если рамку сделали из неё: заливка, обводка и контур. */
  readonly vector?: VectorBoxParams;
  /** Служебные боксы запечённых эффектов, снятые при назначении; их rect — в той же системе координат, что `rect`. */
  readonly effectTiles?: readonly CustomObjectTemplateBox[];
  /** Рамка в момент назначения: по разнице с нынешней плитки едут за передвинутым слоем. */
  readonly rect?: CustomTemplateBoxRect;
  /** Поворот рамки в момент назначения; нет поля — снимок снят без позы, и плитки едут только сдвигом и масштабом. */
  readonly rotation?: number;
  /** Зеркало рамки в момент назначения: по разнице с нынешним плитки отражаются вслед за слоем. */
  readonly flipHorizontal?: boolean;
  readonly flipVertical?: boolean;
};

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

/** Affine 2D `[xx, xy, yx, yy, tx, ty]` (PSD TySh / SVG matrix / Konva.Transform). */
export type TransformMatrix = readonly [number, number, number, number, number, number];

export type TextAnchor = {
  readonly kind: 'baseline' | 'boxTopLeft';
  readonly x: number;
  readonly y: number;
};

/** `split` — фамилия отдельной строкой от имени с отчеством, `perPart` — каждая часть имени своей строкой. */
export const FIO_LINE_BREAKS = ['single', 'split', 'perPart'] as const;

export const FIO_ORDERS = ['surnameFirst', 'givenFirst'] as const;

export type FioLayout = {
  order: (typeof FIO_ORDERS)[number];
  lineBreak: (typeof FIO_LINE_BREAKS)[number];
  includePatronymic: boolean;
  initials?: boolean;
  /** With `split` / `perPart`: a double surname breaks after its hyphen onto the next line. Off by default. */
  hyphenBreak?: boolean;
  /** ФИО всегда одной строкой: формат роли задаёт порядок и инициалы, но не перенос (карточка цитаты «снизу»). */
  keepLine?: true;
};

export type TextBoxParams = {
  fontName: string;
  postScriptName?: string;
  fontWeight: string;
  fontSize: number;
  /** Raw pt from Photoshop Character panel (without user free-transform scale) */
  fontSizePt?: number;
  lineHeightPx: number;
  /** Raw pt leading from Photoshop (without user free-transform scale) */
  lineHeightPt?: number;
  /**
   * `justify` — выключка по ширине (PSD `justify-*`): межсловные интервалы растягиваются до края
   * контейнера на всех строках абзаца, кроме последней. Рендерит движок (WASM/Rust);
   * legacy-рендеры (Konva/SVG/бэкенд) её не знают и деградируют в `left`.
   */
  align: 'left' | 'center' | 'right' | 'justify';
  color: Color; //TODO: research
  italic: boolean;
  textType?: TextTypeEnum | VignetteTextTypeEnum;
  fioLayout?: FioLayout;
  textMode?: 'block' | 'free';
  /** Блочный текст, чья рамка идёт за содержимым при вводе; снимается, когда пользователь задаёт ширину сам. */
  autoWidth?: boolean;
  autoFit?: boolean;
  maxFontSize?: number;
  verticalAlign?: 'top' | 'center' | 'bottom';
  /** Рамка цитаты карточки серии виньетки (R409): кегль печати выбирает заливка по ёмкостям ниже. */
  vignetteQuote?: boolean;
  /** Ёмкость рамки цитаты карточки в символах при номинальном кегле. */
  quoteNominalCapacity?: number;
  /** Ёмкость рамки цитаты карточки при наименьшем кегле — предел длины цитаты (R430). */
  quoteFloorCapacity?: number;
  /** Наименьший кегль цитаты рамки (R430), px шаблона: на нём считана `quoteFloorCapacity`. */
  quoteFloorFontSize?: number;
  text?: string;
  underline?: boolean;
  strikethrough?: boolean;
  /** PSD TextStyle.FillFlag: `false` = прозрачная заливка (Fill 0%) — глифы не закрашиваются, видны только эффекты. */
  fillFlag?: boolean;
  /** PSD Gradient Overlay текстового слоя: при рендере перекрывает `color` (как в PS эффект поверх заливки). */
  fillGradient?: VectorFillData;
  /** Градиент, снятый тумблером «Градиент» в панели; включение возвращает его. Рендер и печать поле не читают. */
  fillGradientDisabled?: VectorFillData;
  /**
   * Photoshop faux bold — stroke-симуляция жирного начертания (для шрифтов без Bold-варианта).
   * Независим от `fontWeight`: применяется поверх любого weight'а. Renderer добавляет
   * stroke = color, stroke-width = fontSize * 4% к glyph path.
   */
  fauxBold?: boolean;
  tracking?: number;
  /** Canvas-px, не pt (в отличие от lineHeightPt) — legacy-фолбэк для lineHeightPx. */
  leading?: number;
  /**
   * Photoshop "Type Options" caps (`FontCaps`): `1` = small caps, `2` = all caps.
   * Renderer uppercases text on render; faux small-caps без OpenType `smcp` тоже uppercase.
   */
  fontCaps?: number;
  /**
   * Photoshop "Type Options" baseline (`FontBaseline`): `1` = superscript, `2` = subscript.
   * Renderer scales fontSize ~×0.6 and shifts baseline.
   */
  fontBaseline?: number;
  /**
   * Photoshop Character panel "Horizontally scale" (HorizontalScale): glyph X scale.
   * `1` = 100%, `0.98` = -2% (типичный приём дизайнера для уплотнения строки).
   * Renderer применяет к advance width (для wrap) и к glyph paths (визуально).
   */
  horizontalScale?: number;
  /**
   * Photoshop Character panel "Vertically scale" (VerticalScale): glyph Y scale.
   * `1` = 100%. Renderer применяет к высоте глифов; line-leading НЕ масштабирует —
   * это требует независимой реализации (followup).
   */
  verticalScale?: number;
  /**
   * Photoshop LayerTextData.orientation: вертикальное письмо (CJK). Отсутствие = горизонталь (дефолт).
   * Renderer выкладывает глифы колонками справа-налево с накоплением по Y вместо X.
   */
  orientation?: 'vertical';
  /**
   * Photoshop ResourceDict.SuperscriptSize (per-layer) — fontSize multiplier for `fontBaseline=1`.
   * PS-default 0.583. Если undefined — renderer fallback'ает к 0.583.
   */
  superscriptSize?: number;
  /**
   * Photoshop ResourceDict.SuperscriptPosition (per-layer) — baseline shift up
   * как доля от fontSize (renderer применяет dy = -fontSize * superscriptPosition).
   * PS-default 0.333.
   */
  superscriptPosition?: number;
  /** Photoshop ResourceDict.SubscriptSize (per-layer). PS-default 0.583. */
  subscriptSize?: number;
  /**
   * Photoshop ResourceDict.SubscriptPosition (per-layer) — baseline shift down
   * как доля от fontSize. PS-default 0.333.
   */
  subscriptPosition?: number;
  /**
   * Photoshop ResourceDict.SmallCapSize (per-layer) — fontSize multiplier для строчных при `fontCaps=1`.
   * PS-default 0.7. Renderer fallback'ает к 0.7 при undefined.
   */
  smallCapSize?: number;
  /**
   * Photoshop LayerTextData.useFractionalGlyphWidths. `true` (default) — sub-pixel advance,
   * `false` — округление advance до целого пикселя на каждом глифе. Влияет на ширину строки и wrap.
   */
  useFractionalGlyphWidths?: boolean;
  /**
   * PSD point text, centre/right aligned and tracked: the import hung the frame on Photoshop's pen box (the line's
   * alignment point moved by its share of the tracking), not on the text matrix origin. Absent — on the origin.
   */
  frameOnPenBox?: boolean;
  /**
   * PSD LayerTextData.antiAlias mode:
   * `0` None / `1` Sharp / `2` Crisp / `3` Strong / `4` Smooth.
   */
  antiAlias?: number;
  /** Point text from a PSD: rect top → first baseline in em of `fontSize` (the anchor, not the ink).
   *  Absent — see `firstBaselineRule`. */
  firstBaselineOffset?: number;
  /** PSD area `'ascent'` ('d' height), PSD point `'ink'`, InDesign frame its First Baseline Offset type; absent — cap. */
  firstBaselineRule?: 'ascent' | 'ink' | InddFirstBaselineRule;
  /** InDesign frame's Min first baseline offset, em of `fontSize`. */
  firstBaselineMin?: number;
  /** Photoshop text rules a PSD import composed this box by (`TEXT_LAYOUT_*` in text-layout): `1` — exact-frame wrap;
   *  `2` — also a right/centre point line ends at its pen (anchored on the PSD origin, trailing spaces count) and each
   *  style run draws with its own font. A later rule raises the number the import writes and gates on it, so saved
   *  boxes never reflow. Absent — the legacy rules, unchanged. */
  textLayout?: number;
  // ─── ParagraphStyle (per-layer) ───
  /** First-line indent, canvas px (same space as `fontSize`). */
  firstLineIndent?: number;
  /** Left side indent всех строк, canvas px. */
  startIndent?: number;
  /** Right side indent всех строк, canvas px. */
  endIndent?: number;
  /** Vertical gap до параграфа, canvas px. */
  spaceBefore?: number;
  /** Vertical gap после параграфа, canvas px. */
  spaceAfter?: number;
  /** Auto-hyphenation enable. */
  autoHyphenate?: boolean;
  /** Min word length для hyphenate. */
  hyphenatedWordSize?: number;
  /** Min letters before hyphen. */
  preHyphen?: number;
  /** Min letters after hyphen. */
  postHyphen?: number;
  /** Max consecutive hyphenated lines. */
  consecutiveHyphens?: number;
  /** PSD ParagraphStyle zone, copied as PSD stores it; no renderer reads it, so it does not ride the font scale. */
  hyphenationZone?: number;
  /** Photoshop `Language` of the layer style its words hyphenate by (runs override); stamped by the import with Hyphenate on. */
  language?: number;
  /** WordSpacing min/opt/max для justify. */
  wordSpacing?: readonly number[];
  /** LetterSpacing min/opt/max для justify. Конфликт с layer-level `tracking` — для justify-mode. */
  paragraphLetterSpacing?: readonly number[];
  /** GlyphSpacing min/opt/max для justify. */
  glyphSpacing?: readonly number[];
  /** Leading type: 0 Roman / 1 Bottom-to-bottom. */
  leadingType?: number;
  /** Knuth-Plass composer (true) vs single-line (false, default). */
  everyLineComposer?: boolean;
  /** Hanging punctuation (Roman). */
  hanging?: boolean;
  styleRuns?: TextStyleRun[];
  /**
   * From `textLayout` 7: the paragraphs whose indents, spacing or alignment differ from the box fields above. Written
   * only when the paragraphs really differ; absent — every paragraph takes the box fields.
   */
  paragraphRuns?: TextParagraphRun[];
  /** Scale/skew/rotation. `fontSize`/`lineHeightPx` остаются canvas-px (nominal × scale), не nominal. */
  transform?: TransformMatrix;
  anchor?: TextAnchor;
  /** Path/warp text: глифы распределяются вдоль кривой, не wrapping в bbox. */
  textPath?: TextPathParams;
  /** Area Type on Shape: замкнутый контур контейнера, строки переносятся внутри него. */
  areaShape?: TextAreaShapeParams;
  /** InDesign «Ignore Text Wrap»: the text sets across objects with {@link CustomTemplateBoxBase.textWrap}. */
  ignoreTextWrap?: boolean;
  /** InDesign: the {@link BoxTextWrap.group} tokens of the groups holding this text; their wraps leave it be. */
  textWrapGroups?: string[];
  /** Photoshop Warp Text: the renderer bends the glyph outlines; the text stays editable. */
  warp?: TextWarpParams;
  /** Pre-v1 album residue parked by the persist-normalizer — см. {@link LegacyTextMetrics}. */
  legacyTextMetrics?: LegacyTextMetrics;
  /**
   * Provenance: the CLIENT (student) typed this value, so a rebuild of the master album keeps it instead of
   * overwriting it with the master's word (`preserve-custom-text::isClientEditedText`, `book-generator`).
   */
  editedByClient?: boolean;
  /**
   * Which entry of the album's cover text memory this slot prints (`cover-text-fields::carryCoverTexts`
   * stamps it on the carry) — without it a corrected word comes back to a stranger's place on the next pack.
   */
  memoryEntry?: number;
  /** The template's own text at birth — `spread-content-memory::isDefaultTemplateText` tells an untouched placeholder by it. */
  templateText?: string;
  /** Lead quote line under the teachers' band (R427): fill prints «quote — First Patronymic»; absent — an ordinary LEAD_QUOTE. */
  leadLine?: boolean;
};

/** Раскладка ФИО имеет смысл только у поля ФИО, а перенос строк ломает автоширину: один закон на меню, мост и Layer A. */
export const fioLayoutTextPatch = (fioLayout: FioLayout): Pick<TextBoxParams, 'textType' | 'fioLayout' | 'autoWidth'> => ({
  textType: TextTypeEnum.PERSON_FULL_NAME,
  fioLayout,
  autoWidth: false,
});

/**
 * Метрики старого `drawable.text`, которым в v1 нет честного слота; пишет и читает только
 * persist-нормализатор `@lite-workspace/photobook-model` ради обратимости legacy → v1 байт-в-байт.
 * `fontSize`/`lineHeightPx` — отставшие дубликаты (на stage расходятся с `naturalAligns.fontSize`
 * в половине случаев). `lineHeight`/`letterSpacing` кодек переводит делением на `fontSize` —
 * при неположительном `fontSize` деление необратимо, исходные значения ложатся сюда.
 */
export type LegacyTextMetrics = {
  fontSize?: number;
  lineHeightPx?: number;
  lineHeight?: number;
  letterSpacing?: number;
};

/**
 * One paragraph's own attributes (`textLayout` 7): `[from, to)` in UTF-16 code units of `text` — the paragraph without its
 * `\n`, soft returns (U+2028) inside; a field left out inherits the box field. Same px space as the box fields.
 */
export type TextParagraphRun = {
  from: number;
  to: number;
  firstLineIndent?: number;
  startIndent?: number;
  endIndent?: number;
  spaceBefore?: number;
  spaceAfter?: number;
  align?: TextBoxParams['align'];
};

export type TextStyleRun = {
  from: number;
  to: number;
  fontName?: string;
  fontSize?: number;
  /** The run's leading, canvas px: from `textLayout` 4 a line steps down by the largest of its runs. */
  lineHeightPx?: number;
  color?: string;
  fontWeight?: string;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  /** Photoshop faux bold — stroke-симуляция жирного. Независим от fontWeight. */
  fauxBold?: boolean;
  tracking?: number;
  fontCaps?: number;
  fontBaseline?: number;
  horizontalScale?: number;
  verticalScale?: number;
  /** Photoshop TextStyle.Language index (Adobe text-engine order): 0 English: USA, 12 Spanish, 14 English: UK, 18 Russian. */
  language?: number;
  /**
   * Photoshop TextStyle.StyleRunAlignment — определяет baseline-метрику для выравнивания
   * run внутри строки при mixed-size:
   *   `0` Bottom · `1` Roman baseline (default) · `2` Center · `3` Top · `4` Em-box top.
   */
  styleRunAlignment?: number;
  /** PSD TextStyle.AutoKerning. */
  autoKerning?: boolean;
  /** PSD TextStyle.Kerning explicit value в 1/1000 em. */
  kerning?: number;
  /** PSD TextStyle.Ligatures — стандартные `liga`. */
  ligatures?: boolean;
  /** PSD TextStyle.DLigatures — discretionary `dlig`. */
  dLigatures?: boolean;
  /** PSD TextStyle.NoBreak — атомарный run в wrap. */
  noBreak?: boolean;
  /** PSD TextStyle.StrokeColor (hex). */
  strokeColor?: string;
  /** PSD TextStyle.FillFlag. */
  fillFlag?: boolean;
  /** PSD TextStyle.StrokeFlag. */
  strokeFlag?: boolean;
  /** PSD TextStyle.FillFirst — порядок fill→stroke vs stroke→fill. */
  fillFirst?: boolean;
  /** PSD TextStyle.OutlineWidth, canvas px (same space as `fontSize`). */
  outlineWidth?: number;
  /** PSD TextStyle.YUnderline thickness/offset. */
  yUnderline?: number;
  /** PSD TextStyle.BaselineShift per-run, canvas px like `fontSize` (separate from `fontBaseline` flag). */
  baselineShift?: number;
};

/**
 * Photoshop Area Type on Shape — текст «залит» внутрь произвольной векторной фигуры.
 * Семантика противоположна {@link TextPathParams} (там `d` — baseline-кривая для глифов):
 * здесь `d` — ЗАМКНУТЫЙ контур контейнера; renderer переносит строки по пересечению
 * строчной полосы с контуром.
 *
 * Координаты нормированы к rect бокса: (0,0) = левый-верхний угол rect, (1,1) = правый-нижний.
 * Поэтому move/resize бокса масштабируют контур автоматически — `d` никогда не переписывается
 * (в отличие от canvas-координатного `textPath.d` с его shift-механикой).
 */
export type TextAreaShapeParams = {
  /** SVG path (абсолютные M/L/C/Z), замкнутый контур в нормированных [0..1] координатах rect. */
  d: string;
};

/** Photoshop Warp Text styles (ag-psd `WarpStyle` ids, menu order). */
export type TextWarpStyle =
  | 'arc'
  | 'arcLower'
  | 'arcUpper'
  | 'arch'
  | 'bulge'
  | 'shellLower'
  | 'shellUpper'
  | 'flag'
  | 'wave'
  | 'fish'
  | 'rise'
  | 'fisheye'
  | 'inflate'
  | 'squeeze'
  | 'twist';

/**
 * Photoshop Warp Text (TySh `warp`). `bounds` = the warp frame (PSD text bounds) normalized to the box
 * rect like `areaShape` ((0,0) = rect top-left, (1,1) = bottom-right), so move/resize/scale carry it
 * with no rewrite; renderer (Rust `text_warp.rs`) bends glyph outlines inside it. Percents: -100..100.
 */
export type TextWarpParams = {
  style: TextWarpStyle;
  /** Bend. */
  value: number;
  horizontalDistortion?: number;
  verticalDistortion?: number;
  /** PSD `warpRotate` = vertical. */
  vertical?: boolean;
  bounds: { x: number; y: number; width: number; height: number };
};

export type TextPathSource =
  | 'textPath'
  | 'warp-arc'
  | 'warp-arcUpper'
  | 'warp-arcLower'
  | 'warp-arch'
  | 'warp-flag'
  | 'warp-rise'
  | 'warp-wave'
  | 'warp-shellUpper'
  | 'warp-shellLower';

export type TextPathParams = {
  /** SVG path d-string в canvas-coord (M ... C ... Z). Готов к подстановке в <path d="..."/>. */
  d: string;
  /** Закрытая (Z) или открытая кривая. Закрытая → текст может пойти по кругу (truncation policy на open path обрезает). */
  closed: boolean;
  /**
   * @deprecated — use pathRangeStart/pathRangeEnd. Heuristic-based legacy field, used as fallback
   * when pathRange* отсутствует.
   */
  startOffset?: number;
  /**
   * Start of text container along path в долях arc-length [0..1], derived from PSD
   * `textPath.data.textRange[0]` через bezier arc-length integration (а не path-параметр напрямую —
   * для неравномерных сегментов это ×2 разница). Renderer выбирает позицию текста внутри
   * `[pathRangeStart, pathRangeEnd]` по `paragraphStyle.justification`.
   */
  pathRangeStart?: number;
  /**
   * End of text container along path в долях arc-length. Может быть > 1 для закрытого пути,
   * когда text wraps past start. Renderer берёт `(pathRangeEnd - pathRangeStart)` как доступную
   * arc-length для размещения глифов.
   */
  pathRangeEnd?: number;
  /**
   * PSD `textPath.data.pathData["7"]` — flip baseline (текст идёт по пути в обратном
   * направлении, baseline снизу пути). Renderer инвертирует tangent и направление обхода.
   */
  flip?: boolean;
  /** Источник — на runtime для дебага и метрик. */
  source: TextPathSource;
};

/**
 * Photoshop layer-effect "Contour" curve point. Both `x` (input intensity) and `y`
 * (output intensity) are in the PS-native 0..255 domain (verified against ag-psd's
 * own read/write fixtures — `x`/`y` are plain `doub` descriptor values, not percent units).
 */
export type EffectContourPoint = { readonly x: number; readonly y: number; readonly corner?: boolean };

export type BoxShadow = {
  /** Resolved Photoshop light angle and authored distance, before legacy offset rounding. */
  angle?: number;
  distance?: number;
  /** Normalized 0..1. */
  noise?: number;
  /** Preserved metadata; donor shadow AA is not implemented. */
  antialiased?: boolean;
  layerConceals?: boolean;
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
  opacity?: number;
  inner?: boolean;
  enabled?: boolean;
  /**
   * Spread (для drop shadow) / Choke (для inner shadow) в процентах 0..100.
   * Photoshop "Spread" расширяет сплошную часть тени перед blur — при 100%
   * тень становится твёрдой силуэтной без градиента, при 0% — чистый gaussian blur.
   */
  spread?: number;
  /**
   * Blend mode как CSS-совместимое имя (`multiply`, `screen`, `overlay`, ...).
   * Для рендера через Konva маппится в `globalCompositeOperation`. По умолчанию
   * тень в Photoshop использует `multiply` — имитирует затемнение от
   * физического объекта, блокирующего свет.
   */
  blendMode?: string;
  /** PS "Contour" — кастомный профиль спада тени после blur. Linear/отсутствие = прежняя линейная рампа. */
  contour?: readonly EffectContourPoint[];
};

export type BoxStroke = {
  overprint?: boolean;
  pattern?: BoxPatternOverlay['pattern'];
  patternId?: string;
  patternName?: string;
  patternScale?: number;
  patternLinked?: boolean;
  patternPhaseX?: number;
  patternPhaseY?: number;
  patternPhaseUnits?: { x?: { units: string; value: number }; y?: { units: string; value: number } };
  blendMode?: string;
  color: string;
  width: number;
  opacity?: number;
  enabled?: boolean;
  /**
   * Photoshop "Position" обводки — относительно края слоя.
   *
   * - `outside` — обводка растёт наружу, визуально увеличивая границу слоя.
   * - `center` — обводка распределена поровну снаружи и внутри.
   * - `inside` — обводка растёт внутрь слоя, не меняя его габариты.
   *
   * Для PSD с outside/inside SVG/Konva рендер без учёта position визуально
   * отличается от Photoshop. Сейчас поле только прокидывается парсером —
   * рендер разных режимов это followup.
   */
  position?: 'outside' | 'center' | 'inside';
  /**
   * Углы обводки при нулевом скруглении рамки — настройка редактора, из PSD не читается.
   * `square` (умолчание) — острые; `round` — внешний край скругляется на толщину линии, как в Photoshop.
   */
  corners?: 'square' | 'round';
  /** Photoshop "Fill Type" обводки; отсутствует/`color` — сплошной цвет из `color`. */
  fillType?: 'color' | 'gradient' | 'pattern';
  /** Заливка при `fillType='gradient'` — та же геометрия, что у `gradientOverlay` (linear angle/reverse). */
  gradient?: {
    colors: string[];
    angle?: number;
    reverse?: boolean;
    /** `shapeburst` runs across the stroke from its inner edge to its outer edge (live render only). */
    style?: 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond' | 'shapeburst';
    gradient?: BoxEffectGradient;
    scale?: number;
    align?: boolean;
    offset?: { x: number; y: number };
    dither?: boolean;
    interpolationMethod?: BoxGradientOverlay['interpolationMethod'];
    referenceBounds?: BoxGradientOverlay['referenceBounds'];
    documentBounds?: BoxGradientOverlay['documentBounds'];
    referenceTransform?: BoxGradientOverlay['referenceTransform'];
    documentTransform?: BoxGradientOverlay['documentTransform'];
  };
};

export type BoxGlow = {
  technique?: 'softer' | 'precise';
  source?: 'edge' | 'center';
  range?: number;
  noise?: number;
  jitter?: number;
  antialiased?: boolean;
  gradient?: BoxEffectGradient;
  color: string;
  blur: number;
  opacity?: number;
  inner?: boolean;
  enabled?: boolean;
  /**
   * Blend mode эффекта (`screen`, `linear dodge`, ...) — как composite'ит его Photoshop
   * поверх backdrop'а. Дефолт PS для glow — screen; запекание normal-пикселями гасит
   * неон на тёмном фоне, поэтому парсер выносит такой glow в отдельный underlay-бокс.
   */
  blendMode?: string;
  /** Photoshop "Spread"/"Choke" в процентах 0..100 — доля blur, занятая сплошным ореолом до размытия. */
  choke?: number;
  /** PS "Contour" — кастомный профиль спада свечения после blur. Linear/отсутствие = прежняя линейная рампа. */
  contour?: readonly EffectContourPoint[];
};

export type BoxBevel = {
  /** Photoshop gloss-contour AA metadata; retained even where the renderer has no AA implementation. */
  antialiasGloss?: boolean;
  /** Ширина фаски в px (PS "Size"). */
  size: number;
  /** Размытие результата в px (PS "Soften"). */
  soften?: number;
  /** Направление света в градусах (PS math-координаты, 0° = справа). */
  angle?: number;
  /** Высота света над плоскостью 0..90° (PS "Altitude"). */
  altitude?: number;
  /** PS "Depth" как множитель (1 = 100%). */
  depth?: number;
  /** up = выпуклый, down = вдавленный. */
  direction?: 'up' | 'down';
  style?: 'outer bevel' | 'inner bevel' | 'emboss' | 'pillow emboss' | 'stroke emboss';
  technique?: string;
  useShape?: boolean;
  useTexture?: boolean;
  shapeContour?: readonly EffectContourPoint[];
  shapeRange?: number;
  shapeAntialiased?: boolean;
  texture?: Pick<BoxPatternOverlay, 'pattern' | 'patternId' | 'patternName' | 'scale' | 'phaseX' | 'phaseY'> & {
    depth?: number;
    invert?: boolean;
    linked?: boolean;
  };
  /** PS "Gloss Contour" — remaps the highlight/shadow shading profile. Linear/отсутствие = прежняя рампа. */
  contour?: readonly EffectContourPoint[];
  highlightColor?: string;
  highlightOpacity?: number;
  /** Blend блика — PS-дефолт screen; в корпусе встречается color dodge. */
  highlightBlendMode?: string;
  shadowColor?: string;
  shadowOpacity?: number;
  /** Blend тени фаски — PS-дефолт multiply; в корпусе встречается color burn. */
  shadowBlendMode?: string;
  enabled?: boolean;
};

export type BoxSatin = {
  angle?: number;
  distance?: number;
  blendMode?: string;
  antialiased?: boolean;
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
  opacity?: number;
  invert?: boolean;
  enabled?: boolean;
  /** PS "Contour" — кастомный профиль волны satin после blur. Linear/отсутствие = прежняя линейная рампа. */
  contour?: readonly EffectContourPoint[];
};

export type BoxEffectGradient =
  | {
      type: 'solid';
      name?: string;
      smoothness?: number;
      colorStops: { color: string; location: number; midpoint: number }[];
      opacityStops: { opacity: number; location: number; midpoint: number }[];
      /** 32-bit source: stops are shown values of linear light, blended in linear light under this tone curve. */
      linearLight?: 'srgb' | 'gray';
    }
  | {
      type: 'noise';
      name?: string;
      roughness?: number;
      colorModel?: 'rgb' | 'hsb' | 'lab' | 'hsl';
      randomSeed?: number;
      restrictColors?: boolean;
      addTransparency?: boolean;
      min: number[];
      max: number[];
    };

export type BoxPatternOverlay = {
  pattern?: { width: number; height: number; pixelData?: string; fileId?: string; url?: string; data?: Uint8ClampedArray };
  patternId?: string;
  patternName?: string;
  scale?: number;
  opacity?: number;
  enabled?: boolean;
  phaseX?: number;
  phaseY?: number;
  angle?: number;
  align?: boolean;
  linked?: boolean;
  blendMode?: string;
};

/** Source controls use ag-psd fractions: 100% = 1, including offsets and stop midpoints. */
export type BoxGradientOverlay = {
  rendering?: 'live';
  referenceBounds?: { x: number; y: number; width: number; height: number };
  documentBounds?: { x: number; y: number; width: number; height: number };
  referenceTransform?: [number, number, number, number, number, number];
  documentTransform?: [number, number, number, number, number, number];
  /** Legacy render palette; noise gradients have no solid-color approximation. */
  colors: string[];
  angle?: number;
  opacity?: number;
  scale?: number;
  enabled?: boolean;
  type?: 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond';
  blendMode?: string;
  reverse?: boolean;
  align?: boolean;
  offset?: { x: number; y: number };
  dither?: boolean;
  interpolationMethod?: 'classic' | 'perceptual' | 'linear' | 'smooth';
  /** Source metadata only; presence does not opt a legacy box into live rendering. */
  gradient?: BoxEffectGradient;
};

export type BoxColorOverlay = { color: string; opacity?: number; enabled?: boolean; blendMode?: string; rendering?: 'live' };

export type BoxEffects = {
  shadows?: BoxShadow[];
  strokes?: BoxStroke[];
  glows?: BoxGlow[];
  bevel?: BoxBevel;
  satin?: BoxSatin;
  /** Topmost Photoshop instance; the remaining ones follow in `extraColorOverlays`. */
  colorOverlay?: BoxColorOverlay;
  /** Photoshop instances after `colorOverlay`, in Layer Style list order: each one is painted beneath the previous. */
  extraColorOverlays?: BoxColorOverlay[];
  gradientOverlay?: BoxGradientOverlay;
  /** Same contract as `extraColorOverlays`, for gradient overlays after `gradientOverlay`. */
  extraGradientOverlays?: BoxGradientOverlay[];
  patternOverlay?: BoxPatternOverlay;
};

export type LayerStyleMask = {
  /** Editor binding; absent follows the source layer. This is not PSD positionRelativeToLayer. */
  linked?: boolean;
  dataUrl?: string;
  url?: string;
  fileId?: string;
  bounds: { x: number; y: number; width: number; height: number };
  transform: [number, number, number, number, number, number];
  defaultOpacity: number;
  density?: number;
  feather?: number;
  invert?: boolean;
  disabled?: boolean;
  positionRelativeToLayer?: boolean;
};

export type LayerStyleMasks = { source?: LayerStyleMask[]; post?: LayerStyleMask[] };

export type BoxClippingState = {
  isClipped: boolean;
  isClippingBase: boolean;
};

export type ObjectAssetVersions = {
  // fileId хранится рядом с path, чтобы удалить устаревший файл из storage при перезапекании превью
  thumb?: { path: string; width: number; height: number; fileId?: string };
  medium?: { path: string; width: number; height: number; fileId?: string };
  large?: { path: string; width: number; height: number; fileId?: string };
  thumbHash?: string;
  // provenance + freshness: какой ветке принадлежит превью и из какой версии модели отрендерено
  source?: 'server-assembly' | 'sync' | 'edit' | 'replace-file';
  updatedAt?: string;
  format?: 'webp' | 'jpeg';
  modelVersion?: string;
  // Opaque ownership token: only the renderer holding the current token may replace this spread preview.
  previewGeneration?: string;
  // выставляется при постановке рендера в очередь; свежая запись рендера перетирает entry целиком и снимает флаг
  stale?: boolean;
  // Claim wall-clock time (ISO): the stuck-preview sweeper ages an entry by this stamp, falling back to
  // modelVersion for claims written before the field existed. Re-stamped by every accepted claim.
  claimedAt?: string;
  // How many times the stuck-preview sweeper already re-triggered this claim. A genuinely NEW claim
  // (another generation) resets it to 0; a sweeper retry preserves/increments it; past the configured
  // cap the entry goes terminal (un-staled when it has a thumb, dropped when it has none).
  recoveryAttempts?: number;
  // Copy-on-write contract: while this field is present the preview objects still belong to the demo
  // reference — a holder must never displace them, or one save in one copy would delete the reference's
  // previews for everyone. Absence means «own», so entries written before the field read correctly.
  inheritedFrom?: { projectId: string; photobookId: string };
  // Carried by a LANDED render, never by a claim: it describes the pixels on disk, and a claim draws
  // nothing. Declared by whoever DREW them — the server render callback speaks for the Rust adapter this
  // deployment ships, an editor commit speaks for the bundle that tab loaded — because a writer stamping
  // its own constant over a pre-bump capture would pin old pixels as current. Absent (or null, which the
  // two merge-writers store to overwrite a stale claim) = rendered before the rule was versioned or by an
  // unknown producer, and both read as «behind».
  renderRuleVersion?: number | null;
  // True = the server render substituted at least one font (callback `substitutedFonts` non-empty; Rust
  // counts every unresolved face except the fallback itself, Inter Regular, a nameless layer included):
  // still shown, not canonical. False = the writer's own verdict, and the two measure differently: the
  // server says false only when nothing was substituted, a full editor commit says false because it passed
  // the editor's gate (CURABLE_FONT_ERRORS only — a nameless layer is not counted). Always explicit, since
  // the merge writer keeps a key it does not receive. Absent = a producer that does not write the key
  // (photobook previews, a 2-tier commit, rows older than the field) — read as not degraded.
  fontsDegraded?: boolean;
};

export type LightweightSpreadPreview = {
  // uuid слота модели, которому принадлежит запись: даёт клиенту сшивать флаги с настройками по имени
  // разворота, а не по позиции — reorder между двумя сейвами позиции путает. Отсутствует у старых
  // ответов; null-слот (нет ни превью, ни пометки) остаётся null и uuid не несёт.
  uuid?: string;
  thumb: string | null;
  thumbHash: string | null;
  medium?: string | null;
  large?: string | null;
  width?: number | null;
  height?: number | null;
  stale?: boolean;
  // Перезапекание кладёт рендер по тому же ключу в storage; без метки версии клиент вечно показывает
  // закэшированную (immutable на сутки) картинку прошлого рендера.
  updatedAt?: string | null;
  // Серверная пометка «требует внимания» (settings.spreads[].needsAttention / settings.cover.needsAttention):
  // разворот потерял фото при переносе лиц. Едет рядом с превью, потому что карточка альбома читает только
  // этот список — тянуть ради флага целые settings она не станет. Снимается сохранением разворота.
  needsAttention?: boolean;
  // Слот обложки альбома «Без фотообложки» (`model.cover_by_lab`): обложку делает типография, рендера у неё
  // не будет и ждать его нечего — карточка рисует заглушку, «превью готовы» этот слот не держит.
  coverByLab?: boolean;
};

/**
 * Embedded original payload for a PSD smart object.
 * One per unique placed-layer source (`Layer.placedLayer.id`).
 * Instances share via `SmartObjectBoxParams.sourceRef`.
 */
export type TemplateSmartObjectSource = {
  id: string;
  fileName: string;
  mimeType: 'image/jpeg' | 'image/png' | 'application/octet-stream';
  naturalWidth: number;
  naturalHeight: number;
  data?: string;
  url?: string;
  fileId?: string;
  // Renditions of the SOURCE, not of the box: a renderer swapping one in must divide the linear part of
  // `SmartObjectBoxParams.transform` by tier/natural, or the affine places the smaller image at full-size scale.
  versions?: ObjectAssetVersions;
};

// Photoshop keeps most smart-object sources in its own PSB container: neither createImageBitmap nor
// the server-side color normalizer decodes it, so only a raster source may stand in for a box image.
export function isRenderableSmartObjectSource(source: Pick<TemplateSmartObjectSource, 'mimeType'> | null | undefined): boolean {
  return source?.mimeType === 'image/png' || source?.mimeType === 'image/jpeg';
}

/**
 * Recipe for re-baking `maskDataUrl` (and divide-blend baked `pixelData`) when a text
 * leaf inside the clipping-base group is edited. Set by `applyCompoundClipMasks` when
 * the base group contains text layers — without it, an edit of e.g. "2023"→"2026" leaves
 * the rendered clipper silhouette stale (still showing the old "2023" outline).
 *
 * Used by runtime re-bake util (canvas-engine) to recompute the group composite when any
 * `sourceTextBoxId` changes, without re-parsing the original PSD.
 */
export type CompoundClipBakeRecipe = {
  /** Source PSD masks cannot be rebuilt from live glyphs; retain the imported silhouette. */
  preserveStaticMask?: boolean;
  /**
   * Box ids of text leafs inside the clipping-base group. When any of these text-boxes
   * changes its `params.text` (or font/size/color), the clipper needs re-bake.
   */
  sourceTextBoxIds: readonly string[];
  /**
   * Box ids of all leafs (text + pixel) inside the clipping-base group, in PS panel order (top
   * leaf first). Runtime re-bake reads pixel data from these boxes (or rasterizes text-boxes
   * fresh) and composes via Porter-Duff over to rebuild the mask.
   */
  leafBoxIds: readonly string[];
  /**
   * Clipping-base group bounds in PSD canvas coordinates. Legacy cropped textures
   * share this rect; retained full-footprint text textures keep their own source rect.
   * Legacy rebakes use the clipper rect; live-source rebakes preserve an independent compatibility-mask frame.
   */
  groupBounds: { left: number; top: number; right: number; bottom: number };
  /**
   * Original blend mode of THIS clipper. When `requiresRgbComposite=true`, runtime
   * re-bake must run `bakeClipperPixels` with this blendMode (e.g. divide) to redo the
   * baked `pixelData`; otherwise only the alpha mask is rebuilt.
   */
  clipperBlendMode: string;
  /**
   * True when this clipper's blendMode requires full RGB composite of the base group
   * (e.g. `divide`) — runtime must rebuild groupComposite (RGB+A), not just alpha.
   */
  requiresRgbComposite: boolean;
};

export type ObjectBoxParams = {
  /** @deprecated TRANSIENT base64; persisted asset lives in `versions`/`url`. Removed in Phase 3. */
  pixelData?: string;
  url?: string; // URL загруженного изображения
  fileId?: string; // ID загруженного файла
  versions?: ObjectAssetVersions;
  /**
   * Base64 PNG of the alpha-clipping mask. Set when this OBJECT is a non-photo clipper
   * within a Photoshop clipping group (e.g. multiply tint above the photo placeholder).
   * Renderer applies it via destination-in so the overlay takes the silhouette of the
   * original base layer — same mask the IMAGE/SMART_OBJECT photo in the group uses.
   * Legacy inline form; new imports persist the mask by reference in `maskUrl`/`maskFileId`.
   */
  maskDataUrl?: string;
  maskRect?: BoxMaskRect;
  /** Uploaded alpha-clipping mask (alpha-only PNG in storage); preferred over `maskDataUrl`. */
  maskUrl?: string;
  maskFileId?: string;
  /**
   * Set when `maskDataUrl` was computed from a clipping-base GROUP composite that
   * contained text leafs. Runtime re-bake util consumes this to refresh `maskDataUrl`
   * (and `pixelData` for divide blends) when any referenced text-box edits.
   */
  compoundClipBakeRecipe?: CompoundClipBakeRecipe;
  /**
   * Поля запечки вокруг слоя: тень, свечение и внешняя обводка ушли в пиксели плитки, поэтому rect бокса —
   * плитка, а границы самого слоя — rect минус эти поля. «Рамка изображения» срезает их обратно: иначе
   * рамка встаёт на размер тени. Пишет парсер, только когда поля ненулевые.
   */
  bakeInsets?: { left: number; top: number; right: number; bottom: number };
  /**
   * Только у фона: его rect — вся плитка, а не слой внутри неё (фон из декора или из рамки на всю плитку). Рамка из
   * такого фона несёт признак дальше (`FrameDecorSource.fullTile`), и обратный ход в декор её не растит.
   */
  fullTile?: true;
  /**
   * Параметры стиля слоя, впечатанного парсером в пиксели плитки (`effects` у такого бокса пусты). Рендер их
   * не читает; «Рамка изображения» поднимает их в живые эффекты рамки — правило `parsedBoxEffects`.
   */
  rawEffects?: BoxEffects;
  /**
   * Чистая альфа растрового слоя ДО впечатывания эффектов — силуэт, который рамка получает при «Тип
   * элемента → Рамка изображения». Пишет парсер только для слоя с непрямоугольной альфой и
   * внешними эффектами: у такого слоя альфа плитки уже растянута обводкой и тенью, и восстановить форму из
   * неё нельзя; слой без эффектов редактор читает прямо из своих пикселей. Это НЕ маска самого декора
   * (`maskDataUrl` выше): плитка рисуется как есть. Инлайновая форма — `maskDataUrl`, загруженная —
   * `maskUrl`/`maskFileId`, как у трио маски.
   */
  frameSilhouette?: FrameSilhouette;
  /** Фото-слот clipping-группы: id слоя-основы (`<slot>-clipbase`), в который он вставлен — `ImageBoxParams.maskBaseBoxId`. */
  maskBaseBoxId?: string;
  /**
   * Set on the photo-slot OBJECT produced from a Photoshop clipping group when auto
   * IMAGE-classification is disabled. Carries the data needed to convert
   * this decor into a real IMAGE frame on user action. Presence of this field also
   * signals the editor to expose the "Convert to image frame" action.
   */
  imageFrameIntent?: ImageFrameIntent;
  /**
   * True when this background/object was explicitly applied by the user (not part of cover/template design).
   * populated/consumed by @lite-workspace/photobook-model persist-normalizer and by the box-form background
   * application in @lite-workspace/shared/photobook-shared;
   * template-authoring path (spread-to-template.ts) must never write this
   */
  userApplied?: boolean;
  /**
   * True when this background drawable came from a design pack (auto distribution or template apply), as
   * opposed to being baked into the collage's own layout template. Origin discriminator for the full-bleed
   * migration: a pack background's honest aspect lives in its asset versions, never in the collage template.
   * populated/consumed by @lite-workspace/photobook-model persist-normalizer and by the box-form background
   * application in @lite-workspace/shared/photobook-shared.
   */
  packApplied?: boolean;
  /**
   * Where the worn background came from: the pack spread it was taken off and that spread's pack. Written by
   * the background application when a photographer picks a tile, read at album load to re-dress the spread
   * from the pack's CURRENT look — without the pair, an edited pack spread would keep showing the old file.
   */
  sourceSpreadId?: string;
  sourcePackId?: string;
  /**
   * The rect is where a person put it, not where the layout left it. A full-size spread auto-fits its
   * background to the page, and the photobook normalizer snaps a page-sized container back onto the page —
   * both must leave a deliberate placement alone, so screen and print keep showing what the designer set.
   */
  placed?: boolean;
};

/** Плитка декора, из которой сделали рамку: всё, что нужно, чтобы вернуть слой в прежний вид. */
export type FrameDecorSource = {
  url?: string;
  fileId?: string;
  versions?: ObjectAssetVersions;
  /** TRANSIENT base64 рамки, которую сделал парсер; нормализатор ассетов выгружает её в `url`/`fileId`/`versions`. */
  pixelData?: string;
  /** Поля запечённых эффектов: по ним бокс вырастает обратно до размера плитки. */
  bakeInsets?: { left: number; top: number; right: number; bottom: number };
  /** Рамка осталась на всю плитку (слой не сужали по `bakeInsets`): обратный ход её не растит. Нет поля — сужали. */
  fullTile?: true;
  /**
   * Поля, которые рамка срезала до видимой в Photoshop части слоя (маски групп, место содержимого смарт-объекта):
   * обратный ход возвращает бокс на размер самого слоя. В параметры декора не переезжают — это не эффекты.
   */
  windowInsets?: { left: number; top: number; right: number; bottom: number };
  /** Слой, ставший рамкой по своему контуру: его габарит и угол, угол рамки и центр слоя в осях рамки. */
  layerPose?: {
    rect: CustomTemplateBoxRect;
    rotation: number;
    frameRotation: number;
    centreOffset?: readonly [number, number];
  };
};

export type FrameSilhouette = {
  maskDataUrl?: string;
  maskUrl?: string;
  maskFileId?: string;
};

export type ImageFrameIntent = {
  sourceRef?: string;
  /** @deprecated TRANSIENT base64; persisted source asset lives in `versions`. Removed in Phase 3. */
  previewPixelData?: string;
  /** @deprecated use `versions.thumbHash`. Removed in Phase 3. */
  previewThumbHash?: string;
  /** Persisted source-photo asset for "Convert to image frame": S3 paths + thumbHash. */
  versions?: ObjectAssetVersions;
  paths?: readonly BezierPath[];
  orientation?: 'h' | 'v' | 'r';
  /** Decorative frame stashed across an IMAGE→OBJECT round-trip so a type change can't drop it. */
  frameUrl?: string;
  frameFileId?: string;
  frameWindow?: { x: number; y: number; width: number; height: number };
  /**
   * Эффекты слоя из PSD как их разобрал парсер. Сами эффекты он запекает в пиксели (`bakeDecorative`),
   * и назначение слоя рамкой эти пиксели выбрасывает — фото встаёт на их место. Параметры остаются здесь,
   * чтобы «Рамка изображения» подняла обводку и тени живыми и панель дала их править.
   */
  rawEffects?: BoxEffects;
};

export type SmartObjectBoxParams = {
  /** @deprecated TRANSIENT base64; persisted asset lives in `versions`/`url`. Removed in Phase 3. */
  pixelData?: string;
  url?: string;
  fileId?: string;
  sourceType?: string;
  sourceId?: string;
  originalWidth?: number;
  originalHeight?: number;
  versions?: ObjectAssetVersions;
  sourceRef?: string;
  transform?: TransformMatrix;
  rawEffects?: BoxEffects;
  /**
   * Base64 PNG of the alpha-clipping mask. Set when this smart-object is the photo content
   * of a Photoshop clipping group — the renderer applies it via destination-in over the
   * embedded JPEG so the photo takes the silhouette of the original base layer
   * (e.g. decorative rounded "drawn-edges" frame).
   * Legacy inline form; new imports persist the mask by reference in `maskUrl`/`maskFileId`.
   */
  maskDataUrl?: string;
  /** Uploaded alpha-clipping mask (alpha-only PNG in storage); preferred over `maskDataUrl`. */
  maskUrl?: string;
  maskFileId?: string;
  /**
   * Photoshop smart-filter stack, bottom (applied first) to top; enabled filters only. The baked layer pixels
   * already hold the result — the renderer runs the stack only when it draws the raw source through `transform`.
   * An `unsupported` entry keeps the source path off: the box draws its own (Photoshop-filtered) pixels.
   */
  smartFilters?: readonly SmartObjectFilter[];
};

/** One live smart filter; `radius` in document px. */
export type SmartObjectFilter = { readonly opacity?: number } & (
  | { readonly kind: 'gaussianBlur'; readonly radius: number }
  | { readonly kind: 'brightness'; readonly brightness: number; readonly contrast: number; readonly useLegacy?: boolean }
  | { readonly kind: 'invert' }
  /** Levels / Color Balance / Hue/Saturation / Selective Color, in the adjustment-layer params shape. */
  | { readonly kind: 'adjustment'; readonly params: AdjustmentBoxParams }
  | { readonly kind: 'unsupported'; readonly name: string }
);

/** PS blend-if band `[lo0, lo1, hi0, hi1]` (0..255), same order as ag-psd `blendingRanges`. */
export type PsdBlendIfBand = readonly [number, number, number, number];

export type PsdBlendIfChannel = { readonly this: PsdBlendIfBand; readonly under: PsdBlendIfBand };

/** PS Blending Options › Blend If, per channel (gray first). Stored only when it differs from the full ranges. */
export type PsdBlendIf = {
  readonly gray: PsdBlendIfChannel;
  readonly r: PsdBlendIfChannel;
  readonly g: PsdBlendIfChannel;
  readonly b: PsdBlendIfChannel;
};

/**
 * Composite semantics of a PSD layer group kept as a group. Mode, opacity, Fill, styles and Blend Interior
 * stay on the box's own `blendMode`/`opacity`/`fillOpacity`/`effects`/`blendInteriorElements` — no duplicates here.
 * Absent block = a plain organisational group, rendered exactly as before.
 */
export type PsdGroupComposite = {
  readonly v: 1;
  /** `false` = Pass Through. */
  readonly isolated: boolean;
  /** Group mask; `rect` is in group coordinates and moves with the group. */
  readonly mask?: {
    readonly ref: string;
    /** Import only: the encoded mask before upload; the asset normalizer moves it into `ref` + `fileId`. */
    readonly dataUrl?: string;
    readonly fileId?: string;
    readonly rect: CustomTemplateBoxRect;
    /** Degrees clockwise about the rect centre — the group itself keeps rotation 0, a group turn lands here. */
    readonly rotation?: number;
    readonly invert?: boolean;
    readonly density?: number;
    readonly feather?: number;
    readonly vectorRef?: string;
  };
  /** Group vector mask; knots (normalized `[Y, X, …]`) in group coordinates, moving with the group like `mask`. */
  readonly vectorMask?: NonNullable<AdjustmentBoxParams['maskVector']>;
  readonly blendIf?: PsdBlendIf;
  readonly knockout?: 'shallow' | 'deep';
  /**
   * Knockout only. `canvas` = the document's bottom layer was no Photoshop Background, so a deep knockout (and a
   * shallow one at the top level) reveals the page beneath, not the BACKGROUND box. Absent = it reveals that box.
   */
  readonly knockoutBackdrop?: 'canvas';
  /** Only `false` is stored: Photoshop's default is `true`. */
  readonly blendClipped?: false;
  readonly transparencyShapes?: false;
  /** Excluded channels bitmask (PS `brst`). */
  readonly channels?: number;
  /** Layer / Vector Mask Hides Effects (PS `lmgm` / `vmgm`): that mask hides the styled group instead of shaping its styles. */
  readonly layerMaskHidesEffects?: true;
  readonly vectorMaskHidesEffects?: true;
  /**
   * PS artboard: the group draws only inside `rect` (group coordinates, moving with the group like `mask.rect`),
   * over its board background (`#rrggbb`; absent = transparent).
   */
  readonly artboard?: { readonly rect: CustomTemplateBoxRect; readonly rotation?: number; readonly background?: `#${string}` };
};

export type GroupBoxParams = {
  personId: UUID | null;
  peopleType: GroupPeopleType;
  psdGroup?: PsdGroupComposite;
  /**
   * Род кадра, вставшего в рамки группы по лестнице фоллбека генерации, когда своих кадров структурной
   * роли не хватило. Ставит генерация, читают панель фото и правила перетаскивания; поля нет — группа
   * держит свой структурный род. Персистится вместе с `peopleType`, иначе род теряется на первом же
   * прогоне модели через нормализатор.
   */
  adoptedPeopleType?: GroupPeopleType;
  /** Крупная карточка владельца в серии карточек с цитатами (обычная ячейка ученика, R429); поля нет — обычная ячейка. */
  ownerCard?: boolean;
  /**
   * Клетка выращенной ячейки повёрнутого пака вокруг центра фото, px шаблона: шаг дизайнера в масштабе ячейки.
   * Ставит рост сетки, читает разворот продолжения — по расставленным ячейкам шаг не измерить, между ними промежутки.
   */
  tiltSlot?: readonly [number, number, number, number];
  /** Вылет ячейки повёрнутого пака за клетку `tiltSlot`, px шаблона: вылет дизайнера в масштабе ячейки. */
  tiltSpill?: readonly [number, number];
  /** Карточка владельца из ячейки пака «Владелец альбома» (место класса, `ownerCard`), а не карточка сборки Люмир. */
  packOwnerCard?: boolean;
};

type BoxRectX = number;
type BoxRectY = number;
type BoxRectW = number;
type BoxRectH = number;

export type CustomTemplateBoxRect = [BoxRectX, BoxRectY, BoxRectW, BoxRectH];

export enum TemplateKind {
  COVER = 'cover',
  VIGNETTES = 'vignette',
  // TEACHERS = 'teachers',
  DEFAULT = 'default',
  // TEXT = 'text',
  /** Личный разворот: шаблон с рамкой владельца, экземпляр на каждого ученика. */
  PERSONAL = 'personal',
}

export enum TemplateType {
  HALF = 'half',
  FULL = 'full',
}

export function isHalfTemplate(template: { type?: TemplateType } | null | undefined): boolean {
  return template?.type === TemplateType.HALF;
}

/**
 * Effective editor lock of a single box. Structural on purpose — the render adapters see a box through
 * their own shadow types, and every one of them must resolve the background default the same way.
 */
export function isBoxLocked(box: { type: string; locked?: boolean }): boolean {
  return box.locked ?? box.type === BoxType.BACKGROUND;
}

export type Point = { readonly x: number; readonly y: number };

export type BBox = {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
};

export type RawColor = {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
};

export type VectorOrigination =
  | { readonly kind: 'rectangle'; readonly bbox: BBox }
  | { readonly kind: 'roundedRect'; readonly bbox: BBox; readonly radii: readonly [number, number, number, number] }
  | { readonly kind: 'ellipse'; readonly bbox: BBox }
  | { readonly kind: 'line'; readonly from: Point; readonly to: Point }
  | { readonly kind: 'path' };

export type GradientStop = { readonly offset: number; readonly color: RawColor };

export type VectorFillData =
  | { readonly kind: 'solid'; readonly color: RawColor }
  | {
      readonly kind: 'linearGradient';
      readonly stops: readonly GradientStop[];
      /** Сырой PS-угол (компас y-вверх: 90° = offset-1 СВЕРХУ); рендерер отражает sin — закон в ps-effects README. */
      readonly angle: number;
      /** PS gradient-overlay Scale: `1` = 100%; растяжение оси градиента относительно bbox слоя. */
      readonly scale?: number;
    }
  | { readonly kind: 'radialGradient'; readonly stops: readonly GradientStop[]; readonly center: Point; readonly radius: number }
  | {
      readonly kind: 'pattern';
      readonly patternId: string;
      readonly scale: number;
      readonly angle: number;
      /** PS Link with Layer: tiles from the layer origin instead of the document origin. */
      readonly linked?: boolean;
      readonly phaseX?: number;
      readonly phaseY?: number;
      /** Pattern pixels, filled at import like `BoxPatternOverlay.pattern`. */
      readonly pattern?: BoxPatternOverlay['pattern'];
    }
  | {
      /**
       * PS gradient fill of a shape, as stored: the renderer samples it like a live gradient overlay, laid out in
       * the shape's path bounds (`align`, the PS default) or in `documentBounds`.
       */
      readonly kind: 'gradient';
      readonly style: 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond';
      readonly gradient: BoxEffectGradient;
      readonly angle: number;
      /** `1` = 100%. */
      readonly scale: number;
      readonly reverse?: boolean;
      readonly align?: boolean;
      /** Fraction of the frame, as `BoxGradientOverlay.offset`. */
      readonly offset?: { readonly x: number; readonly y: number };
      readonly dither?: boolean;
      readonly interpolationMethod?: string;
      readonly documentBounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
    };

export type VectorStrokeData = {
  readonly width: number;
  readonly color: RawColor;
  readonly alignment: LineAlignment;
  readonly lineCap: LineCapType;
  readonly lineJoin: LineJoinType;
  readonly dashPattern?: readonly number[];
  readonly fill?: VectorFillData;
};

export type VectorBoxParams = {
  readonly origination: VectorOrigination;
  readonly paths: readonly BezierPath[];
  readonly fill?: VectorFillData;
  readonly stroke?: VectorStrokeData;
  readonly fillOpacity?: number;
  readonly pixelData?: string;
  readonly url?: string;
  readonly fileId?: string;
  /** Обтравочная маска как в Photoshop: id основы (фигура или узел группы ниже), чьим силуэтом движок режет фигуру. */
  readonly maskBaseBoxId?: string;
  /**
   * Эффекты слоя из PSD как их разобрал парсер. Сами эффекты он запекает в пиксели (`bakeDecorative`),
   * и назначение слоя рамкой эти пиксели выбрасывает — фото встаёт на их место. Параметры остаются здесь,
   * чтобы «Рамка изображения» подняла обводку и тени живыми и панель дала их править.
   */
  readonly rawEffects?: BoxEffects;
};

export enum BoxType {
  IMAGE = 'image',
  TEXT = 'text',
  OBJECT = 'object',
  BACKGROUND = 'background',
  GROUP = 'group',
  SMART_OBJECT = 'smartObject',
  VECTOR = 'vector',
  ADJUSTMENT = 'adjustment',
}

/**
 * Box kinds whose params reference a design-pack asset row (fileId / maskFileId / frame / imageFrameIntent).
 * `vector` carries a `fileId` too but is deliberately out: nothing produces one for it, and adding it would
 * change both the reparse delete sweep and read-path enrichment, which is a change of its own.
 */
export const ASSET_BOX_TYPES: readonly BoxType[] = [BoxType.OBJECT, BoxType.BACKGROUND, BoxType.IMAGE, BoxType.SMART_OBJECT];

/**
 * Подложка разворота: в глазах фотографа фон и декор — один слой, поэтому клик по фону подменяет их
 * вместе, а «Убрать фон» снимает их вместе. Фото, рамки изображений, тексты и векторы к подложке не
 * относятся и переживают обе операции.
 * Строки совпадают со значениями `DrawableData.type` v0-формы, поэтому предикат обслуживает обе формы.
 */
export const DECOR_BOX_TYPES: readonly BoxType[] = [BoxType.OBJECT, BoxType.SMART_OBJECT, BoxType.BACKGROUND];

export function isDecorBoxType(type: BoxType | string): boolean {
  return (DECOR_BOX_TYPES as readonly string[]).includes(type);
}

/**
 * Свой декор фотографа — тот, у которого нет провенанса шаблона: проекция шаблона на разворот штампует
 * `idFromTemplate: box.id` каждому боксу (`shared-box-converters`, `generate-drawable-background`), поэтому
 * декор пака и PSD-шаблона всегда помечен, а положенный руками — нет. То же правило, что у
 * `isOwnDecorBox` (template-document lifecycle.ops), только про подложку, а не про переезд через корешок.
 * Свой декор не снимает ни смена фона, ни «Убрать фон»: подложка — это то, что положил сам фон.
 * Фон под правило не попадает — он и ЕСТЬ подложка, его меняют и убирают всегда.
 * Поле спелено одинаково в обеих формах и едет через кодек в `carriedBoxFields`, поэтому предикат
 * обслуживает и боксы, и drawable.
 */
export function isOwnDecor(item: { type?: string; idFromTemplate?: string } | undefined): boolean {
  if (!item || item.type === undefined || item.type === BoxType.BACKGROUND) return false;
  return isDecorBoxType(item.type) && item.idFromTemplate === undefined;
}

/**
 * Слой разворота (ADR 0045) — фон или бокс с меткой `spreadWide`: живёт в голове коллажа 0 под обеими страницами
 * и не принадлежит ни одной. Одно правило «закреплён внизу разворота» для раскола, пересборки, порядка слоёв и
 * панели «Слои»; строки типа совпадают с `DrawableData.type` v0-формы, поэтому предикат обслуживает обе формы.
 */
export function isSpreadWideBox(item: { type?: string; spreadWide?: boolean } | undefined): boolean {
  return item !== undefined && (item.type === BoxType.BACKGROUND || item.spreadWide === true);
}

/**
 * Рамка изображения или текстовый блок — контент разворота, в извлечённый фон они не попадают вовсе (а не
 * прячутся в нём). Это одна из проб правила «что убрать из фона» — `toBackgroundBoxes` (`background-boxes.ts`):
 * помимо рамок и текстов оно снимает плоские заглушки под фото с их обрамлением и клипперы снятых баз;
 * самостоятельно предикат ответа «что остаётся в фоне» не даёт. Парсерная пометка `backgroundExcluded` шире
 * (она метит ещё и декор, и не-полноканвасные заливки, и подложки эффектов), поэтому предикат читает тип
 * бокса и frame-intent напрямую.
 * IMAGE и OBJECT с `imageFrameIntent` — это photo-slot из `template-kind.detector`; помещённый растровый smart object
 * — фото сверх него. Строки совпадают со значениями `DrawableData.type` v0-формы, поэтому предикат обслуживает обе формы.
 */
export function isFrameOrTextBox(box: FrameOrTextProbe | undefined): boolean {
  if (box?.type === BoxType.TEXT || box?.type === BoxType.IMAGE) return true;
  if (box?.type === BoxType.OBJECT) return box.params?.imageFrameIntent !== undefined;
  if (box?.type === BoxType.SMART_OBJECT) return smartObjectConvertsToImage(box);
  return false;
}

/**
 * Помещённый smart object — фото-контент и становится рамкой фото, если только связанный файл не векторная
 * графика: у вектора нет растра, который встал бы на место фото. Одно правило на извлечение фона, смену типа
 * бокса в обоих редакторах и их меню.
 */
export function smartObjectConvertsToImage(box: SmartObjectProbe | undefined): boolean {
  if (box?.type !== BoxType.SMART_OBJECT) return false;
  return !(box.params && SOURCE_TYPE_KEY in box.params && box.params[SOURCE_TYPE_KEY] === 'vector');
}

// Любой бокс шаблона и проба `isFrameOrTextBox` — без приведения: params у разных типов разные.
type SmartObjectProbe = { type?: string; params?: object };
// Ключ привязан к полю типа: переименование `sourceType` сломает компиляцию здесь, а не молча включит вектор.
const SOURCE_TYPE_KEY = 'sourceType' satisfies keyof SmartObjectBoxParams;

/** Ниже этого порога элемент матрицы размещения считается нулём (или единицей): шум float, а не наклон. */
export const PLACED_MATRIX_EPS = 1e-9;

/**
 * Положение smart object из PSD: Photoshop поворачивает и масштабирует его матрицей `params.transform`, а
 * `rect` — осевой габарит запечённого растра (вместе с полями эффектов), `rotation` у такого бокса 0.
 * Рамка встаёт повёрнутым прямоугольником того же центра: угол — направление первой стороны матрицы,
 * размеры — натуральные × её масштаб; скос пропадает (высота берётся перпендикулярной первой стороне).
 * Матрица без поворота и масштаба ничего не говорит: редактор сбрасывает её в сдвиг при любом перемещении
 * (geometry.ops), а `rect` уже живой. Не трогаем обрезанный кадром растр (наклонённое содержимое шире
 * `rect`); маску не смотрим: где она ляжет на наклонённой рамке, решает тот, кто рамку ставит.
 */
export function placedSmartObjectOutline(
  box: CustomSmartObjectTemplateBox,
): { readonly rect: CustomTemplateBoxRect; readonly rotation: number } | undefined {
  const { transform, originalWidth: w0, originalHeight: h0 } = box.params;
  if (!transform || !w0 || !h0 || w0 <= 0 || h0 <= 0) return undefined;
  const [a, b, c, d] = transform;
  const eps = PLACED_MATRIX_EPS;
  if (Math.abs(a - 1) < eps && Math.abs(b) < eps && Math.abs(c) < eps && Math.abs(d - 1) < eps) return undefined;
  const scaleX = Math.hypot(a, b);
  const det = a * d - b * c;
  if (!(scaleX > eps) || !(Math.abs(det) > eps)) return undefined;
  const [x, y, rectWidth, rectHeight] = box.rect;
  const fits = 1;
  if (Math.abs(a * w0) + Math.abs(c * h0) > rectWidth + fits || Math.abs(b * w0) + Math.abs(d * h0) > rectHeight + fits) {
    return undefined;
  }

  // Прямоугольник симметричен при повороте на 180°: из двух углов берём меньший, иначе зеркальный слой
  // (первая сторона смотрит влево) лёг бы рамкой вверх ногами.
  let angle = (Math.atan2(b, a) * 180) / Math.PI;
  if (angle > 90) angle -= 180;
  else if (angle <= -90) angle += 180;
  const width = w0 * scaleX;
  const height = (h0 * Math.abs(det)) / scaleX;
  const [cx, cy] = [x + rectWidth / 2, y + rectHeight / 2];
  return { rect: [cx - width / 2, cy - height / 2, width, height], rotation: box.rotation + angle };
}

/** The outline, refused to a box with a mask: that mask is drawn over the axis tile and already carries the tilt. */
export function placedSmartObjectGeometry(
  box: CustomSmartObjectTemplateBox,
): { readonly rect: CustomTemplateBoxRect; readonly rotation: number } | undefined {
  if (box.params.maskDataUrl || box.params.maskUrl || box.params.maskFileId) return undefined;
  return placedSmartObjectOutline(box);
}

// Запечённый в пиксели или тайлы стиль лежит в `params.rawEffects`: смена типа их выбрасывает и поднимает параметры оттуда.
export function parsedBoxEffects(box: CustomTemplateBox): BoxEffects | undefined {
  if (box.layerStyleRendering === 'live') return box.effects;
  if (box.effects) return box.effects;
  return 'rawEffects' in box.params ? box.params.rawEffects : undefined;
}

/** Заливка страницы, которую редактор кладёт первым боксом разворота («Цвет фона»). */
export const PAGE_FILL_BOX_ID = 'page-fill';

type FrameOrTextProbe = { type?: string; params?: { imageFrameIntent?: unknown; sourceType?: string } };

/**
 * InDesign Text Wrap of an object. `box` — around its frame, `contour` — around `contour`, `jump` — no text beside it;
 * `side` — where text may go (`largest` — the wider side). `offsets` — canvas px the text keeps away, top/left/bottom/
 * right (`contour` keeps `offsets[0]` all round). `contour` — closed polygon, 0..1 of the object's rect. `group` — a
 * group's wrap only: the token its texts list in `textWrapGroups`, so the wrap leaves them be (as InDesign does).
 */
export type BoxTextWrap = {
  mode: 'box' | 'contour' | 'jump';
  side: 'both' | 'left' | 'right' | 'largest';
  offsets: [number, number, number, number];
  contour?: [number, number][];
  group?: string;
};

export type CustomTemplateBoxBase = {
  effectReferencePoint?: { x: number; y: number };
  effectDocumentOrigin?: { x: number; y: number };
  type: BoxType;
  id: string;
  rect: CustomTemplateBoxRect;
  rotation: number;
  visible?: boolean;
  /**
   * Editor lock set by the designer — distinct from `lock`, which mirrors read-only PSD lock state.
   * Absent means "not decided": read it through `isBoxLocked`, which locks a background box by default.
   */
  locked?: boolean;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  opacity?: number;
  fillOpacity?: number;
  blendInteriorElements?: boolean;
  /** Leaf layer Blend If (a group's rides in `params.psdGroup`); drawn by the engine on image/object/vector/background boxes. */
  blendIf?: PsdBlendIf;
  /** Leaf Knockout (a group's rides in `params.psdGroup`); drawn by the engine on image/object/vector/background boxes. */
  knockout?: PsdGroupComposite['knockout'];
  /** Leaf knockout only, as `PsdGroupComposite.knockoutBackdrop`. */
  knockoutBackdrop?: PsdGroupComposite['knockoutBackdrop'];
  /** Leaf Blend Clipped Layers as Group of a clipping base (a group's rides in `params.psdGroup`): clippers blend one by one. */
  blendClipped?: PsdGroupComposite['blendClipped'];
  /** Leaf Transparency Shapes Layer of a live-styled, styled type or knockout leaf (a group's: `psdGroup`): masks = its shape. */
  transparencyShapes?: PsdGroupComposite['transparencyShapes'];
  layerStyleRendering?: 'live';
  layerStyleMasks?: LayerStyleMasks;
  blendMode?: string;
  effects?: BoxEffects;
  psdLayerId?: string;
  lock?: PsdLayerLockState;
  colorLabel?: PsdLayerColorLabel;
  clipping?: BoxClippingState;
  groupId?: string;
  name?: string;
  /** InDesign Text Wrap of this object: the texts of its page set around it (except those with `ignoreTextWrap`). */
  textWrap?: BoxTextWrap;
  /**
   * Decor trim window (object/background/smartObject/vector): the full untrimmed content's placement,
   * same tuple shape as `rect`. Written by @lite-workspace/photobook-model's persist-normalizer and by
   * the design-editor's own edit ops; the ALBUM spread-to-template.ts conversion must never write it.
   */
  contentRect?: CustomTemplateBoxRect;
  /**
   * Parse-time classification: every box that is not a full-canvas layer (decor, плашки, frames, texts,
   * effect underlays). It does not decide what the extracted фон keeps — that is `toBackgroundBoxes`, where
   * decor stays visible.
   */
  backgroundExcluded?: boolean;
  /**
   * Provenance link back to the layout template box this one was minted from. Survives a regeneration, so
   * content-preservation matches a rebuilt spread's boxes to the old ones by it (`preserve-custom-text`,
   * `preserve-person-groups`, `preserve-decoratives`, `spread-content-memory`) and pack-owned content is
   * told apart from user-added content by its presence (`photo-book-helper.facade`, `cover-text-fields`).
   */
  idFromTemplate?: string;
  /** Box came from the layout template rather than from the user — see `hasTemplateObjects` (spread-queries). */
  isTemplateObject?: boolean;
  /**
   * Box is hidden from render and from the readiness gates that count empty frames/texts
   * (`empty-photo-frames`, `empty-text-fields`, `cover-text-fields`, `printed-person-ids`).
   * Distinct from `visible`, which is the designer's own eye toggle.
   */
  skipRender?: boolean;
  /** Решение закона пересчёта обложки под корешок — см. `CoverFit`; пишет photobook-shared, кодек несёт как есть. */
  coverFit?: CoverFit;
  /**
   * Слой пришёл с плитки фона: редактор пака одевает обычный разворот всеми слоями фона (подложка, заливка,
   * декор, группы), и только по этой метке смена или снятие фона отличает их от декора, который дизайнер
   * положил сам. Ставит `applyBackground` пак-редактора, снимает `setTemplateBackground`; кодек альбома и
   * «Сохранить как шаблон» несут её насквозь, альбом сам её не ставит. Кроме `setTemplateBackground`, её читают,
   * среди прочих, рендер-адаптер (`TemplateBoxAdapter`: слой надетого фона лежит под текстом разворота и обтекания
   * не даёт, его `textWrap` в рендер не идёт) и `findEmptySpreads` (такой слой — часть фона, а не содержимое разворота).
   */
  wornFromBackground?: boolean;
  /**
   * Слой разворота (ADR 0045): декор на весь разворот, у которого нет страницы. Стоит головой коллажа 0 в
   * разворотных координатах; раскол, пересборка страницы и замена половинным шаблоном его не делят и не
   * переносят. Фон — частный случай без метки, обоих читает только `isSpreadWideBox`.
   */
  spreadWide?: boolean;
};

export type CustomImageTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.IMAGE;
  params: ImageBoxParams;
  color?: string; //NOTE: baground color
  /** Ориентация рамки: h = horizontal, v = vertical, r = round/square */
  orientation?: 'h' | 'v' | 'r';
};

export type CustomTextTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.TEXT;
  params: TextBoxParams;
};

export type CustomObjectTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.OBJECT;
  params: ObjectBoxParams;
};

export type CustomBackgroundTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.BACKGROUND;
  params: ObjectBoxParams;
};

export type CustomSmartObjectTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.SMART_OBJECT;
  params: SmartObjectBoxParams;
};

export type CustomGroupTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.GROUP;
  id: string;
  rect: CustomTemplateBoxRect;
  params: GroupBoxParams;
  boxes: CustomTemplateBox[];
  /** Whether this group can be dragged in DnD. Defaults to true. Set to false for enlarged/featured photos. */
  draggable?: boolean;
};

export type CustomVectorTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.VECTOR;
  params: VectorBoxParams;
};

export type CurvePoint = readonly [number, number];

export type { AdjustmentBoxParams } from './adjustment-params';

export type CustomAdjustmentTemplateBox = CustomTemplateBoxBase & {
  type: BoxType.ADJUSTMENT;
  params: AdjustmentBoxParams;
};

export type CustomTemplateBox =
  | CustomImageTemplateBox
  | CustomTextTemplateBox
  | CustomObjectTemplateBox
  | CustomBackgroundTemplateBox
  | CustomSmartObjectTemplateBox
  | CustomGroupTemplateBox
  | CustomVectorTemplateBox
  | CustomAdjustmentTemplateBox;

/**
 * Сужение коробки по её `type` — ЕДИНСТВЕННЫЙ экземпляр закона, живущий рядом с самим `BoxType`.
 * Заведён потому, что закон уже разошёлся по репозиторию одиннадцатью локальными копиями (шесть
 * именованных функций, пять inline-предикатов в `filter`/`find`), и каждая следующая пара форм
 * приносила двенадцатую. Копии переключаются отдельным шагом: правка их файлов меняет чужие
 * периметры, а этот экспорт аддитивен и ничьего поведения не трогает.
 *
 * Здесь ТОЛЬКО чистое сужение по типу. Предикаты с дополнительным условием — `isFreeTextBox`
 * (`photobook-shared/spread-operations/preserve-custom-text.ts`), `isDecorativeBox`
 * (`preserve-decoratives.ts`), `isOwnDecorBox` (`shared/template-document/lifecycle.ops.ts`) —
 * это другие законы, и сюда они не сводятся.
 *
 * Покрыты ТРИ члена `BoxType` из восьми — `IMAGE`, `TEXT`, `GROUP`: те, которые понадобились
 * первому потребителю. Чистые сужения по остальным пяти (`BACKGROUND`, `OBJECT`, `VECTOR`,
 * `ADJUSTMENT`, `SMART_OBJECT`) сюда ещё НЕ сведены, и это сказано здесь ровно затем, чтобы
 * следующий шаг расширял канон, а не заводил двенадцатую копию — тем же механизмом закон и
 * разошёлся. Живых таких сужения сегодня два, оба по `BACKGROUND`:
 * `photobook-shared/spread-operations/migrate-legacy-spread-background.ts:215-217` (именованный
 * `isBackgroundBox`) и `photobook-shared/spread-operations/build-cover.ts:453` (inline в `find`).
 */
export function isImageBox(box: CustomTemplateBox): box is CustomImageTemplateBox {
  return box.type === BoxType.IMAGE;
}

export function isTextBox(box: CustomTemplateBox): box is CustomTextTemplateBox {
  return box.type === BoxType.TEXT;
}

export function isGroupBox(box: CustomTemplateBox): box is CustomGroupTemplateBox {
  return box.type === BoxType.GROUP;
}

/**
 * Кадр ростерной ячейки приходит из карточки человека, поэтому тип рамки внутри неё исполнять
 * некому: сборка сажает человека группы, не глядя на роль ребёнка, и оставленный тип напечатал бы
 * портрет ученика под именем педагога, а в редакторе владельца ещё и тратил бы лимит портретов.
 * ЕДИНСТВЕННОЕ определение чистки для боксовой модели — его зовут и редактор альбома, и дизайн-редактор.
 * Массив возвращается тем же, когда снимать нечего: вызывающие держат на этом свои no-op проверки.
 */
export function stripFrameRolesForRoster(boxes: CustomTemplateBox[], peopleType: string | null | undefined): CustomTemplateBox[] {
  if (!isRosterCellRole(peopleType)) return boxes;
  if (!boxes.some(box => box.type === BoxType.IMAGE && box.params.frameRole !== undefined)) return boxes;
  return boxes.map(box => {
    if (box.type !== BoxType.IMAGE || box.params.frameRole === undefined) return box;
    const { frameRole: _role, ...params } = box.params;
    return { ...box, params };
  });
}

/**
 * Origin of a CustomTemplate. Drives gap-slider behaviour: photographer-arranged
 * layouts (design pack, user upload) freeze inner frames; Lumir presets reflow.
 */
export type CustomTemplateSource = 'lumir' | 'design-pack' | 'user';

export type SpreadGuideOrientation = 'h' | 'v';

/** Editor-only ruler guide; never rendered — the renderer consumes boxes, not this field. */
export type SpreadGuide = {
  id: string;
  orientation: SpreadGuideOrientation;
  /** Distance from the spread's left (vertical) or top (horizontal) edge, in book px. */
  positionBookPx: number;
  /** Shared by the copies spread across the pack, so «на всех разворотах» can be undone as one act. */
  groupId?: string;
  /** Locked guides ignore drags and deletes until unlocked. */
  locked?: boolean;
};

/** Сторона стыка картинки фона обложки относительно линий корешка шаблона. */
export type CoverSeam = 'left' | 'right' | 'both' | 'none';

/** Опорная вертикаль обложки: края, границы расстава, линии и середина корешка (refit-template-cover). */
export type CoverAnchorName =
  | 'edge-left'
  | 'rasstav-left'
  | 'spine-left'
  | 'spine-middle'
  | 'spine-right'
  | 'rasstav-right'
  | 'edge-right';

/**
 * Середина полосы обложки, на которой стоял центр элемента: задняя или лицевая сторона, между линией
 * загиба и корешком (`…-center`) либо между линией загиба и расставом (`…-center-rasstav`).
 */
export type CoverCenterName = 'back-center' | 'back-center-rasstav' | 'front-center' | 'front-center-rasstav';

/**
 * Решение закона пересчёта обложки под корешок, прочитанное с авторской геометрии один раз: к каким
 * опорным вертикалям пришпилены края бокса и с каким куском он едет, если не пришпилен. Пересчёт ведёт
 * бокс по этому решению, а не перечитывает уже сдвинутую геометрию.
 */
/** Полоса растра во всю ширину обложки на момент первого чтения закона: от неё считается рост по вертикали. */
export type CoverBand = {
  top: number;
  height: number;
  width: number;
};

export type CoverFit = {
  left?: CoverAnchorName;
  right?: CoverAnchorName;
  group?: string;
  /** Кусок стоял ровно по середине полосы — при пересчёте он держит эту середину, а не долю области. */
  center?: CoverCenterName;
  /** Есть только у растра во всю ширину с полем по вертикали; см. `CoverBand`. */
  band?: CoverBand;
};

/** Направляющие Photoshop из исходника в пикселях шаблона, каждая ось по возрастанию. */
export type AuthoredGuides = {
  /** X вертикальных линий. */
  vertical: number[];
  /** Y горизонтальных линий. */
  horizontal: number[];
};

/** Вид разворота пака, который отмечает фотограф; обложку и виньетку задаёт категория разворота, не он. */
export type SpreadKind = 'spread' | 'personal' | 'common';

/** Что показать фотографу о развороте: обложку и виньетку задаёт категория, остальное — выбранный вид. */
export function spreadKindOf(
  model: Pick<CustomTemplate, 'spreadKind'> | null | undefined,
  category: string | null | undefined,
): SpreadKind | 'cover' | 'vignette' {
  if (category === TemplateKind.COVER) return 'cover';
  if (category === TemplateKind.VIGNETTES) return 'vignette';
  return model?.spreadKind ?? 'spread';
}

export type CustomTemplate = {
  id: string;
  name?: string;
  type: TemplateType;
  kind?: TemplateKind | null;
  /** Поля нет — `spread`. Рамки разворота несут роль вида (`applySpreadKind`), поле хранит сам выбор. */
  spreadKind?: SpreadKind;
  source?: CustomTemplateSource;
  /** Pack the template came from — persisted provenance that scopes pack-owned font resolution. */
  designPackageId?: string;
  originalWidth: number;
  originalHeight: number;
  /** Страница, с которой сохранён половинный шаблон: 0 — левая, 1 — правая. Нет у разворота и старых шаблонов (читать как 0). */
  pageSide?: 0 | 1;
  /**
   * Все направляющие PSD, как они стоят в файле: их показывает обычный разворот, обложка рисует вместо них
   * корешок, загиб и расстав. Нет у разворотов, разобранных до появления поля, и у PSD без направляющих.
   */
  authoredGuides?: AuthoredGuides;
  /**
   * Ширина корешка, ПОД КОТОРУЮ нарисована обложка, в собственных пикселях шаблона.
   *
   * Без неё положение линий корешка в шаблоне неизвестно, и обложка ложится на альбом
   * равномерным растяжением — стык задней и лицевой сторон уезжает с линии корешка.
   * Человеку показываем в миллиметрах, храним в пикселях: у шаблона нет
   * своего dpi, а его пиксели — это система координат `originalWidth`/`originalHeight`.
   *
   * Заполняется при загрузке PSD обложки в пак. Для шаблона того же продукта, что и альбом,
   * корешок восстанавливается из ширины и без этого поля.
   */
  authoredSpineWidth?: number;
  /**
   * Загиб и расстав, ПОД КОТОРЫЕ нарисована обложка, в тех же пикселях шаблона. Дизайнер центрует
   * контент по своим линиям, и когда они не совпадают с полями продукта альбома, пересчёт обязан
   * читать исходник по авторским полям, иначе центр уезжает на разницу полей. Читаются
   * из направляющих PSD при загрузке; пустые — берутся у продукта альбома. Расстав 0 — значение:
   * в макете расстава нет.
   */
  authoredWrapWidth?: number;
  authoredRasstavWidth?: number;
  /**
   * На какой линии корешка нарисован стык картинки фона: `right` — заливка задней стороны до правой
   * линии (штатные обложки), `left` — заливка лицевой до левой, `both` — полоса по корешку, вшитая
   * в картинку (растяжением не спасти), `none` — стыка нет. Штампуется при разборе PSD по растру
   * фонового слоя (там он уже декодирован), а дизайнер может переопределить его в панели «Инфо»
   * — руками или кнопкой «Определить по картинке». Без поля считаем `right`.
   */
  coverSeam?: CoverSeam;
  boxes: CustomTemplateBox[];
  previewVersions?: ObjectAssetVersions;
  smartObjectSources?: readonly TemplateSmartObjectSource[];
  adjustmentResources?: readonly AdjustmentBinaryResource[];
  originalLayerTree?: readonly PsdLayerNode[];
  /** Problem kinds (`SpreadProblemKind`) the photographer ignored on this spread, plus client-only `font:<name>` rows. */
  ignoredChecks?: string[];
  /** Approved flat render of a BACKGROUND spread's visible layers — assembly prefers it over the raw base box. */
  backgroundComposite?: {
    fileId: string;
    url?: string;
    width: number;
    height: number;
    versions?: Record<string, { path: string; width: number; height: number }>;
  };
  /** The source PSD's merged composite as uploaded — the whole spread as the designer saw it; replaced on reparse. */
  originalComposite?: {
    fileId: string;
    width: number;
    height: number;
    versions?: Record<string, { path: string; width: number; height: number }>;
  };
};

export type UserTemplate = {
  id: string;
  name: string;
  data: CustomTemplate[];
};

export interface FileUploadResult {
  id: string;
  name: string;
  pathToFileInStorage: string;
  path: string;
  storageInfo: StorageInfo;
  viewSettings: ViewSettings;
}

interface ViewSettings {
  size: number;
  width: number;
  height: number;
}

interface StorageInfo {
  storage: string;
  storagePublicEndpoint: string;
  storageStaticEndpoint: string;
  storageBucketName: string;
}
